import { useState, useEffect } from "react";
import api from "@/lib/api";
import { F, I, Btn } from "@/lib/bricks";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtDay } from "@/lib/dates";
import { Page, PageHeader, Stats, StatusTabs, Toolbar, Search, SmallSelect, Controls, List, Row, Empty, Loading, FieldLabel, usePalette, humanize } from "@/components/calm/Calm";

const GROUPS = [
  { key: 'active', label: 'Active', stages: ['new', 'contacted', 'qualified', 'demo', 'negotiating'] },
  { key: 'converted', label: 'Converted', stages: ['converted'] },
  { key: 'lost', label: 'Lost', stages: ['lost'] },
  { key: 'all', label: 'All' },
];

const STAGE_LABELS = {
  new:        'New',
  contacted:  'Contacted',
  qualified:  'Qualified',
  demo:       'Demo',
  negotiating:'Negotiating',
  converted:  'Converted',
  lost:       'Lost',
};

const STAGES = Object.keys(STAGE_LABELS);


const EMPTY = { name: '', email: '', phone: '', brokerage_name: '', stage: 'new', notes: '' };

export default function ProspectorsPage() {
  const { p } = usePalette();

  const [prospects, setProspects] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [stageFilter, setStageFilter] = useState('active');
  const [stageSel, setStageSel] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [addModal, setAddModal]   = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [form, setForm]           = useState(EMPTY);
  const [saving, setSaving]       = useState(false);

  const load = () => {
    api.get('/prospectors')
      .then(r => { setProspects(r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!form.name) return toast.error('Name is required');
    setSaving(true);
    try {
      await api.post('/prospectors', form);
      toast.success('Prospect added');
      setAddModal(false);
      setForm(EMPTY);
      load();
    } catch { toast.error('Failed to add prospect'); }
    finally { setSaving(false); }
  };

  const handleEdit = async () => {
    if (!editModal) return;
    setSaving(true);
    try {
      await api.put(`/prospectors/${editModal.id}`, form);
      toast.success('Prospect updated');
      setEditModal(null);
      load();
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const handleStageChange = async (id, stage) => {
    setProspects(ps => ps.map(p => p.id === id ? { ...p, stage } : p));
    api.put(`/prospectors/${id}`, { stage }).catch(() => {
      toast.error('Failed to update stage');
      load();
    });
  };

  const filtered = prospects.filter(pro => {
    const matchesSearch = !search ||
      pro.name.toLowerCase().includes(search.toLowerCase()) ||
      (pro.email || '').toLowerCase().includes(search.toLowerCase()) ||
      (pro.brokerage_name || '').toLowerCase().includes(search.toLowerCase());
    const g = GROUPS.find(x => x.key === stageFilter);
    const matchesStage = (!g || !g.stages || g.stages.includes(pro.stage)) && (stageSel === 'all' || pro.stage === stageSel);
    const matchesSource = sourceFilter === 'all' || (pro.source || 'manual') === sourceFilter;
    return matchesSearch && matchesStage && matchesSource;
  });

  const conversionRate = prospects.length
    ? Math.round((prospects.filter(p => p.stage === 'converted').length / prospects.length) * 100)
    : 0;
  const activeCount = prospects.filter(p => !['converted', 'lost'].includes(p.stage)).length;

  if (loading) return <Loading />;

  const group = GROUPS.find(x => x.key === stageFilter);
  const stageOptions = [{ key: 'all', label: 'Any stage' }, ...STAGES.filter(s => !group || !group.stages || group.stages.includes(s)).map(s => ({ key: s, label: STAGE_LABELS[s] }))];
  const sources = Array.from(new Set(prospects.map(x => x.source || 'manual')));
  const openEdit = (pro) => { setForm({ name: pro.name, email: pro.email, phone: pro.phone || '', brokerage_name: pro.brokerage_name || '', stage: pro.stage, notes: pro.notes || '' }); setEditModal(pro); };

  return (
    <Page testid="prospectors-page">
      <PageHeader
        title="Prospectors"
        subtitle="Potential agents, from first contact to conversion"
        action={<Btn onClick={() => { setForm(EMPTY); setAddModal(true); }} style={{ gap: 8 }}><I name="plus" size={14} />Add prospect</Btn>}
      />

      <Stats items={[
        { label: 'Active', value: activeCount },
        { label: 'Converted', value: `${conversionRate}%` },
        { label: 'Total', value: prospects.length },
      ]} />

      <Controls>
        <StatusTabs options={GROUPS} value={stageFilter} onChange={(k) => { setStageFilter(k); setStageSel('all'); }} testid="prospectors-stage-filter" />
        <Toolbar>
          <Search value={search} onChange={setSearch} placeholder="Search prospects" />
          {stageOptions.length > 2 ? <SmallSelect label="Stage" value={stageSel} onChange={setStageSel} options={stageOptions} /> : null}
          {sources.length > 1 ? <SmallSelect label="Source" value={sourceFilter} onChange={setSourceFilter} options={[{ key: 'all', label: 'Any source' }, ...sources.map(s => ({ key: s, label: humanize(s) }))]} /> : null}
        </Toolbar>
      </Controls>

      {filtered.length === 0 ? (
        <Empty>{search ? `No prospects match "${search}".` : prospects.length ? 'No prospects here.' : 'No prospects yet. Add your first one to start tracking.'}</Empty>
      ) : (
        <List testid="prospectors-list">
          {filtered.map(pro => (
            <Row
              key={pro.id}
              testid="prospect-row"
              onClick={() => openEdit(pro)}
              title={pro.name}
              meta={[pro.email, pro.brokerage_name].filter(Boolean)}
              next={[{ text: pro.last_contact_at ? `Last contact ${fmtDay(pro.last_contact_at)}` : 'Not contacted yet' }, pro.source && pro.source !== 'manual' ? { text: humanize(pro.source) } : null].filter(Boolean)}
              aside={
                <select
                  aria-label={`Stage for ${pro.name}`}
                  value={pro.stage}
                  onClick={e => e.stopPropagation()}
                  onKeyDown={e => e.stopPropagation()}
                  onChange={e => handleStageChange(pro.id, e.target.value)}
                  style={{ border: 'none', background: 'transparent', color: p.ink2, fontFamily: F.display, fontSize: 12.5, cursor: 'pointer', outline: 'none', padding: '2px 0', textAlign: 'right' }}
                >
                  {STAGES.map(s => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
                </select>
              }
            />
          ))}
        </List>
      )}

      {/* Add modal */}
      <Dialog open={addModal} onOpenChange={setAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add prospect</DialogTitle>
            <DialogDescription>Track a potential agent from first contact to conversion.</DialogDescription>
          </DialogHeader>
          <ProspectForm form={form} onChange={f => setForm(f => ({ ...f, ...f }))} setForm={setForm} p={p} />
          <DialogFooter>
            <Btn variant="ghost" onClick={() => setAddModal(false)}>Cancel</Btn>
            <Btn onClick={handleAdd} disabled={saving}>{saving ? 'Saving…' : 'Add prospect'}</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit modal */}
      <Dialog open={!!editModal} onOpenChange={v => { if (!v) setEditModal(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit prospect</DialogTitle>
            <DialogDescription>Update details or stage for {editModal?.name}.</DialogDescription>
          </DialogHeader>
          <ProspectForm form={form} setForm={setForm} p={p} />
          <DialogFooter>
            <Btn variant="ghost" onClick={() => setEditModal(null)}>Cancel</Btn>
            <Btn onClick={handleEdit} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}

function ProspectForm({ form, setForm, p }) {
  const field = (key, label, type = 'text', required = false) => (
    <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <FieldLabel>{label}{required ? ' *' : ''}</FieldLabel>
      <input
        type={type}
        value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        style={{ padding: '9px 12px', borderRadius: 2, border: `1px solid ${p.hair}`, background: p.surf, color: p.ink, fontFamily: F.display, fontSize: 14, outline: 'none' }}
      />
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {field('name',    'Name',    'text',  true)}
      {field('email',   'Email',   'email')}
      {field('phone',   'Phone',   'tel')}
      {field('brokerage_name', 'Office')}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <FieldLabel>Stage</FieldLabel>
        <select
          value={form.stage}
          onChange={e => setForm(f => ({ ...f, stage: e.target.value }))}
          style={{ padding: '9px 12px', borderRadius: 2, border: `1px solid ${p.hair}`, background: p.surf, color: p.ink, fontFamily: F.display, fontSize: 14, outline: 'none' }}
        >
          {STAGES.map(s => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
        </select>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <FieldLabel>Notes</FieldLabel>
        <textarea
          value={form.notes}
          onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          rows={3}
          style={{ padding: '9px 12px', borderRadius: 2, border: `1px solid ${p.hair}`, background: p.surf, color: p.ink, fontFamily: F.display, fontSize: 14, outline: 'none', resize: 'vertical' }}
        />
      </div>
    </div>
  );
}
